/**
 * EntityDetail — full view of an account / contact / project.
 *
 * UI sprint step 6 reskin (2026-05-11): rebuilt against the pwa-v2 entity
 * vocabulary in chrome.css.
 *   - `.ent-hero` flex hero with avatar + .ent-title + .ent-hero-sub
 *   - Edit + Delete actions in .ent-hero-actions
 *   - `.ent-stats` 4-tile stat grid
 *   - `.panel` containers for Next actions, Related, Observations
 *   - `.ent-rel-chip` for relationships, grouped by relation_type
 *   - `.ent-obs-row` per observation, inline edit retained
 *
 * Behavior unchanged: PATCH /api/entities/:id, PATCH /api/observations/:id
 * (CF-1 multi-row fan-out), DELETE /api/entities/:id (H.9 cascade) with
 * 7-day restore. Delete confirm uses the .confirm-panel pattern.
 */
import { useEffect, useState } from 'preact/hooks';
import {
  deleteEntity,
  type EntityDetailResponse,
  getEntity,
  patchEntity,
  patchObservation,
} from '../api.ts';
import { Avatar } from '../components/primitives';
import { Icons } from '../components/icons';

interface EntityDetailProps {
  entityId: string;
  onBack: () => void;
  onSelectEntity: (id: string) => void;
  /**
   * Fires after a successful delete. Receives both the id and the
   * canonical name so the parent can render an Undo toast with the
   * actual entity name post-navigation.
   */
  onDeleted?: (id: string, name: string) => void;
}

export function EntityDetail({
  entityId,
  onBack,
  onSelectEntity,
  onDeleted,
}: EntityDetailProps) {
  const [data, setData] = useState<EntityDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getEntity(entityId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (error) {
    return (
      <div class="v2-page">
        <a class="ent-back" onClick={onBack} style={{ cursor: 'pointer' }}>
          <Icons.Chevron size={12} style={{ transform: 'rotate(180deg)' }} />
          Back
        </a>
        <div class="error-banner">{error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div class="v2-page">
        <a class="ent-back" onClick={onBack} style={{ cursor: 'pointer' }}>
          <Icons.Chevron size={12} style={{ transform: 'rotate(180deg)' }} />
          Back
        </a>
        <div class="ent-hero">
          <div class="ent-hero-body">
            <div class="skel" style={{ width: 240, height: 26, marginBottom: 10 }} />
            <div class="skel" style={{ width: 320, height: 14 }} />
          </div>
        </div>
        <div class="ent-stats">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} class="stat-tile skel" style={{ height: 70 }} />
          ))}
        </div>
      </div>
    );
  }

  const pendingNextAction = data.next_actions[0] ?? null;
  const daysSinceTouch = computeDaysSinceTouch(data.observations);
  const grouped = groupByRelation(data.relationships);

  return (
    <div class="v2-page pop">
      <a class="ent-back" onClick={onBack} style={{ cursor: 'pointer' }}>
        <Icons.Chevron size={12} style={{ transform: 'rotate(180deg)' }} />
        Back
      </a>

      <EditableHero
        entity={data.entity}
        onSave={async (patch) => {
          const updated = await patchEntity(data.entity.id, patch);
          setData({ ...data, entity: updated });
        }}
        onDelete={async () => {
          await deleteEntity(data.entity.id);
          if (onDeleted) onDeleted(data.entity.id, data.entity.name);
          else onBack();
        }}
      />

      {pendingNextAction && (
        <div class="ent-next">
          <div class="ent-next-ico">
            <Icons.Flag size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div class="ent-next-eyebrow">Next action</div>
            <div class="ent-next-text">
              {pendingNextAction.text}
              {pendingNextAction.due_date && (
                <span
                  class="mono"
                  style={{
                    marginLeft: 8,
                    color: 'var(--text-muted)',
                    fontSize: 12,
                  }}
                >
                  · due {pendingNextAction.due_date}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div class="ent-stats">
        <StatTile label="Observations" value={data.observations.length} />
        <StatTile label="Relationships" value={data.relationships.length} />
        <StatTile label="Open" value={data.next_actions.length} sub="actions" />
        <StatTile
          label="Last touch"
          value={daysSinceTouch === null ? '—' : `${daysSinceTouch}d`}
          sub={daysSinceTouch === null ? 'no touches' : 'ago'}
        />
      </div>

      {grouped.length > 0 && (
        <div class="panel ent-panel">
          <div class="panel-head">
            <h3>
              <Icons.Link size={13} />
              Related
              <span class="count">{data.relationships.length}</span>
            </h3>
          </div>
          <div class="panel-body">
            {grouped.map((g) => (
              <div class="ent-rel-group" key={g.relation_type}>
                <div class="ent-rel-label">{g.relation_type.replace(/_/g, ' ')}</div>
                <div class="ent-rel-chips">
                  {g.rows.map((r) => (
                    <a
                      key={r.id}
                      class="ent-rel-chip"
                      onClick={() => onSelectEntity(r.counterpart.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Avatar name={r.counterpart.name} size={18} />
                      <span>{r.counterpart.name}</span>
                      <span class="ent-rel-type">
                        {r.direction === 'outgoing' ? '→' : '←'} {r.counterpart.type}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div class="panel ent-panel">
        <div class="panel-head">
          <h3>
            <Icons.FileText size={13} />
            Observations
            <span class="count">{data.observations.length}</span>
          </h3>
        </div>
        {data.observations.length === 0 ? (
          <div
            style={{
              padding: '24px 16px',
              fontFamily: 'var(--f-serif)',
              fontStyle: 'italic',
              fontSize: 14,
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}
          >
            No observations on this entity yet. Capture something to populate.
          </div>
        ) : (
          <div class="v2-row-list">
            {data.observations.map((o) => (
              <ObservationRow
                key={o.id}
                observation={o}
                onSaved={(updatedContent) => {
                  const editedRow = data.observations.find((x) => x.id === o.id);
                  const oldContent = editedRow?.content;
                  setData({
                    ...data,
                    observations: data.observations.map((x) =>
                      x.content === oldContent
                        ? { ...x, content: updatedContent }
                        : x,
                    ),
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Hero — name + type + edit/delete + inline edit form + delete confirm
// ───────────────────────────────────────────────────────────────────────────

function EditableHero(props: {
  entity: EntityDetailResponse['entity'];
  onSave: (patch: { name?: string; metadata?: Record<string, unknown> }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { entity, onSave, onDelete } = props;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(entity.name);
  const [industry, setIndustry] = useState(String(entity.metadata.industry ?? ''));
  const [tier, setTier] = useState(String(entity.metadata.tier ?? ''));
  const [cadence, setCadence] = useState(
    entity.metadata.cadence_days !== undefined ? String(entity.metadata.cadence_days) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function startEdit() {
    setName(entity.name);
    setIndustry(String(entity.metadata.industry ?? ''));
    setTier(String(entity.metadata.tier ?? ''));
    setCadence(
      entity.metadata.cadence_days !== undefined ? String(entity.metadata.cadence_days) : '',
    );
    setError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const meta: Record<string, unknown> = { ...entity.metadata };
      if (industry.trim()) meta.industry = industry.trim();
      else delete meta.industry;
      if (tier.trim()) meta.tier = tier.trim();
      else delete meta.tier;
      if (cadence.trim() && Number.isFinite(Number(cadence))) {
        meta.cadence_days = Number(cadence);
      } else if (!cadence.trim()) {
        delete meta.cadence_days;
      }

      const patch: { name?: string; metadata?: Record<string, unknown> } = {};
      if (name.trim() && name.trim() !== entity.name) patch.name = name.trim();
      patch.metadata = meta;
      await onSave(patch);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function runDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
    } catch (err) {
      setDeleteError((err as Error).message);
      setDeleting(false);
    }
  }

  if (!editing) {
    return (
      <div class="ent-hero">
        <div class="ent-hero-body">
          <div class="ent-hero-title-row">
            <Avatar name={entity.name} size={32} />
            <h1 class="ent-title">{entity.name}</h1>
            <span class="v2-pill pill-info" style={{ height: 22 }}>
              <span class="dot" />
              {entity.type}
            </span>
          </div>
          <div class="ent-hero-sub">
            {entity.metadata.industry && (
              <span class="mono" style={{ fontSize: 12 }}>
                {String(entity.metadata.industry)}
              </span>
            )}
            {entity.metadata.industry && (entity.metadata.tier || entity.metadata.cadence_days) && (
              <span class="ent-sep">·</span>
            )}
            {entity.metadata.tier && <span>tier {String(entity.metadata.tier)}</span>}
            {entity.metadata.tier && entity.metadata.cadence_days && (
              <span class="ent-sep">·</span>
            )}
            {entity.metadata.cadence_days !== undefined && (
              <span class="mono" style={{ fontSize: 12 }}>
                cadence {String(entity.metadata.cadence_days)}d
              </span>
            )}
          </div>
        </div>
        <div class="ent-hero-actions">
          <button type="button" class="btn" onClick={startEdit}>
            <Icons.Settings size={12} />
            Edit
          </button>
          <button
            type="button"
            class="btn btn-danger-ghost"
            onClick={() => {
              setDeleteError(null);
              setConfirmingDelete(true);
            }}
          >
            Delete
          </button>
        </div>
        {confirmingDelete && (
          <div class="confirm-panel" style={{ marginTop: 14, width: '100%' }}>
            <div
              style={{
                fontFamily: 'var(--f-mono)',
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--danger)',
                marginBottom: 6,
              }}
            >
              Confirm delete
            </div>
            <div
              style={{
                fontSize: 13.5,
                color: 'var(--text-2)',
                lineHeight: 1.5,
                marginBottom: 10,
              }}
            >
              Delete <strong>{entity.name}</strong> and cascade soft-delete to all
              linked observations, relationships, and next actions? Restore is
              available within 7 days.
            </div>
            {deleteError && (
              <div class="error-banner" style={{ marginBottom: 8 }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                class="btn btn-ghost"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn"
                onClick={runDelete}
                disabled={deleting}
                style={{
                  background: 'var(--danger)',
                  borderColor: 'var(--danger)',
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Editing mode
  return (
    <div class="ent-hero">
      <div class="ent-hero-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480 }}>
          <input
            type="text"
            class="input"
            value={name}
            placeholder="Name"
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
          />
          <input
            type="text"
            class="input"
            value={industry}
            placeholder="Industry"
            onInput={(e) => setIndustry((e.target as HTMLInputElement).value)}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              class="input"
              value={tier}
              placeholder="Tier (A/B/C)"
              onInput={(e) => setTier((e.target as HTMLInputElement).value)}
              style={{ flex: 1 }}
            />
            <input
              type="number"
              class="input"
              value={cadence}
              placeholder="Cadence (days)"
              onInput={(e) => setCadence((e.target as HTMLInputElement).value)}
              style={{ flex: 1 }}
            />
          </div>
          {error && <div class="error-banner">{error}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              class="btn btn-ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              onClick={save}
              disabled={saving || !name.trim()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Stat tile
// ───────────────────────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div class="stat-tile">
      <div class="stat-tile-label">{label}</div>
      <div class="stat-tile-value">{value}</div>
      {sub && <div class="stat-tile-sub">{sub}</div>}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Observation row with inline edit
// ───────────────────────────────────────────────────────────────────────────

function ObservationRow(props: {
  observation: EntityDetailResponse['observations'][number];
  onSaved: (content: string) => void;
}) {
  const { observation } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(observation.content);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!draft.trim() || draft.trim() === observation.content) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await patchObservation(observation.id, { content: draft.trim() });
      props.onSaved(updated.content);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="v2-row ent-obs-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginBottom: 4,
          }}
        >
          <span
            class="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--text-faint)',
              letterSpacing: '0.04em',
              textTransform: 'lowercase',
            }}
          >
            {captureMethodLabel(observation.capture_method)}
          </span>
          {observation.is_primary && (
            <span
              class="mono"
              style={{
                fontSize: 9,
                padding: '1px 4px',
                background: 'var(--accent-tint-08)',
                color: 'var(--accent)',
                borderRadius: 2,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              primary
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span
            class="mono"
            style={{ fontSize: 11, color: 'var(--text-muted)' }}
          >
            {new Date(observation.observed_at).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
          {!editing && (
            <button
              type="button"
              class="btn btn-ghost"
              onClick={() => {
                setDraft(observation.content);
                setEditing(true);
              }}
              style={{ height: 22, padding: '0 8px', fontSize: 11 }}
            >
              Edit
            </button>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              class="textarea"
              value={draft}
              onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
              rows={3}
            />
            {error && (
              <div
                class="error-banner"
                style={{ marginTop: 6, marginBottom: 0 }}
              >
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button
                type="button"
                class="btn btn-ghost"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                onClick={save}
                disabled={saving || !draft.trim()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {observation.content}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

interface GroupedRelations {
  relation_type: string;
  rows: EntityDetailResponse['relationships'];
}

function groupByRelation(
  rels: EntityDetailResponse['relationships'],
): GroupedRelations[] {
  const byType = new Map<string, EntityDetailResponse['relationships']>();
  for (const r of rels) {
    const list = byType.get(r.relation_type) ?? [];
    list.push(r);
    byType.set(r.relation_type, list);
  }
  return Array.from(byType.entries())
    .map(([relation_type, rows]) => ({ relation_type, rows }))
    .sort((a, b) => a.relation_type.localeCompare(b.relation_type));
}

function computeDaysSinceTouch(
  obs: EntityDetailResponse['observations'],
): number | null {
  if (obs.length === 0) return null;
  const newest = obs.reduce((acc, o) => {
    const t = new Date(o.observed_at).getTime();
    return Number.isFinite(t) && t > acc ? t : acc;
  }, 0);
  if (!newest) return null;
  return Math.floor((Date.now() - newest) / 86_400_000);
}

function captureMethodLabel(m: string): string {
  switch (m) {
    case 'voice_browser':
      return 'voice';
    case 'voice_whisper':
      return 'voice (whisper)';
    case 'typed':
      return 'typed';
    case 'email_forward':
      return 'email forward';
    case 'meeting_prep':
      return 'meeting prep';
    case 'mcp':
      return 'mcp';
    default:
      return m;
  }
}
